import { Injectable, Inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { PricingDataTransformService } from './pricing-data-transform.service';
import { PricingApiServiceInterface } from './pricing-api.service.interface';
import { PricingDisplayProduct, PricingDisplayRegion, PricingComparisonData } from '../models';
import { PRICING_API_BASE_URL } from '../tokens';

@Injectable({
  providedIn: 'root'
})
export class PricingApiService implements PricingApiServiceInterface {
  
  private apiUrl: string; // Configured API base URL
  
  constructor(
    private http: HttpClient,
    private transformService: PricingDataTransformService,
    @Inject(PRICING_API_BASE_URL) apiBaseUrl: string
  ) {
    // Initialize from Angular environment/provider and normalize
    this.apiUrl = this.normalizeApiBaseUrl(apiBaseUrl || '/api');
  }

  /**
   * Set API base URL from component input, with light normalization.
   * Accepts either a full pricing base (e.g., "/api/pricing") or a generic API base (e.g., "/api").
   * If "/pricing" segment is missing, it will be appended.
   */
  setApiBaseUrl(url: string): void {
    if (!url || typeof url !== 'string') {
      return;
    }
    const normalized = this.normalizeApiBaseUrl(url);
    this.apiUrl = normalized;
  }

  /**
   * Normalize the provided API base URL.
   * - If it already contains "/pricing" segment, return as-is.
   * - Otherwise, append "/pricing" (preserving trailing slash semantics).
   */
  private normalizeApiBaseUrl(url: string): string {
    try {
      // Trim whitespace
      let base = url.trim();
      // If already points to pricing
      if (/\/pricing(\/?$|\/?)/.test(base)) {
        return base.replace(/\/$/, '');
      }
      // Ensure single trailing slash before appending
      if (!base.endsWith('/')) {
        base += '/';
      }
      return `${base}pricing`;
    } catch {
      // Fallback to current apiUrl if normalization fails
      return this.apiUrl;
    }
  }

  // Removed hostname-based URL derivation in favor of Angular DI configuration

  getProducts(regionCode?: string, rangedValue?: number): Observable<PricingDisplayProduct[]> {
    // Note: region/rangedValue params not needed - server returns ALL pricing data
    // We do dynamic lookup in the component based on selected region/rangedValue
    console.log('[PricingApiService] GET', `${this.apiUrl}/products`);
    return this.http.get<any[]>(`${this.apiUrl}/products`).pipe(
      map(adminProducts => {
        console.log('[PricingApiService] raw products count:', Array.isArray(adminProducts) ? adminProducts.length : 0);
        const transformed = adminProducts.map(product => 
          this.transformService.transformProductForDisplay(product)
        );
        console.log('[PricingApiService] transformed products summary:', transformed);
        return transformed;
      })
    );
  }

  getProduct(productCode: string, regionCode?: string, rangedValue?: number): Observable<PricingDisplayProduct> {
    // Note: region/rangedValue params not needed - server returns ALL pricing data
    console.log('[PricingApiService] GET', `${this.apiUrl}/products/${productCode}`);
    return this.http.get<any>(`${this.apiUrl}/products/${productCode}`).pipe(
      map(adminProduct => 
        this.transformService.transformProductForDisplay(adminProduct)
      )
    );
  }

  getPlans(regionCode?: string, rangedValue?: number): Observable<any[]> {
    let params = new HttpParams();
    if (regionCode) params = params.set('region', regionCode);
    if (rangedValue) params = params.set('rangedValue', rangedValue.toString());

    return this.http.get<any[]>(`${this.apiUrl}/plans`, { params });
  }

  getAddons(regionCode?: string, rangedValue?: number): Observable<any[]> {
    let params = new HttpParams();
    if (regionCode) params = params.set('region', regionCode);
    if (rangedValue) params = params.set('rangedValue', rangedValue.toString());

    return this.http.get<any[]>(`${this.apiUrl}/addons`, { params });
  }

  getRegions(): Observable<PricingDisplayRegion[]> {
    // Real API call - use modern admin endpoints
    console.log('API call:', `${this.apiUrl}/regions`);
    return this.http.get<any[]>(`${this.apiUrl}/regions`).pipe(
      map(adminRegions => adminRegions.map(region => 
        this.transformService.transformRegion(region)
      ))
    );
  }

  getComparisonData(
    productCodes: string[], 
    planCodes: string[], 
    regionCode: string, 
    rangedValue?: number
  ): Observable<PricingComparisonData> {
    let params = new HttpParams()
      .set('products', productCodes.join(','))
      .set('plans', planCodes.join(','))
      .set('region', regionCode);
    
    if (rangedValue) params = params.set('rangedValue', rangedValue.toString());

    return this.http.get<any>(`${this.apiUrl}/comparison`, { params }).pipe(
      map(comparisonData => ({
        products: comparisonData.products.map((product: any) => 
          this.transformService.transformProductForDisplay(product)
        ),
        selectedPlans: planCodes,
        selectedRegion: this.transformService.transformRegion(comparisonData.region),
        selectedRangedValue: rangedValue
      }))
    );
  }
}
